export interface IMasterDataProductPriceHistoryProduct {
  productId: number;
  productKey: string | null;
  itemName: string | null;
  upcItemNumber: string | null;
  categoryName: string | null;
}

export interface IMasterDataProductPriceHistorySummary {
  productPrice: number | null;
  effectiveFrom: string | null;
  nextScheduledDate: string | null;
  nextScheduledPrice: number | null;
  inheritedFromBase: boolean;
  appliesTo: string | null;
}

export interface IMasterDataProductPriceHistoryItem {
  productPriceId: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  purchaserId: number | null;
  appliesTo: string | null;
  oldPrice: number | null;
  productPrice: number | null;
  notes: string | null;
  changedBy: string | null;
}

export interface IMasterDataProductPriceHistory {
  product: IMasterDataProductPriceHistoryProduct;
  purchaserId: number | null;
  period: string;
  periodStart: string | null;
  periodEnd: string | null;
  summary: IMasterDataProductPriceHistorySummary;
  items: IMasterDataProductPriceHistoryItem[];
}

export interface ICreatedMasterDataProductPrice {
  productPriceId: number;
  productId: number;
  purchaserId: number | null;
  productPrice: number;
  startDate: string | null;
}

type NullableNumberInput = number | string | null;

function toNumberOrNull(value: NullableNumberInput | undefined): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toTextOrNull(value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  return String(value);
}

export class MasterDataProductPriceHistory {
  private readonly props: IMasterDataProductPriceHistory;

  private constructor(props: IMasterDataProductPriceHistory) {
    this.props = props;
  }

  get product(): IMasterDataProductPriceHistoryProduct {
    return this.props.product;
  }
  get purchaserId(): number | null {
    return this.props.purchaserId;
  }
  get period(): string {
    return this.props.period;
  }
  get periodStart(): string | null {
    return this.props.periodStart;
  }
  get periodEnd(): string | null {
    return this.props.periodEnd;
  }
  get summary(): IMasterDataProductPriceHistorySummary {
    return this.props.summary;
  }
  get items(): IMasterDataProductPriceHistoryItem[] {
    return this.props.items;
  }

  static from(raw: {
    product?: {
      productId?: number | null;
      productKey?: string | null;
      itemName?: string | null;
      upcItemNumber?: string | null;
      categoryName?: string | null;
    } | null;
    purchaserId?: number | null;
    period?: string | null;
    periodStart?: string | null;
    periodEnd?: string | null;
    summary?: {
      productPrice?: NullableNumberInput;
      effectiveFrom?: string | null;
      nextScheduledDate?: string | null;
      nextScheduledPrice?: NullableNumberInput;
      inheritedFromBase?: boolean | null;
      appliesTo?: string | null;
    } | null;
    items?: Array<{
      productPriceId?: number | null;
      effectiveFrom?: string | null;
      effectiveTo?: string | null;
      purchaserId?: number | null;
      appliesTo?: string | null;
      oldPrice?: NullableNumberInput;
      productPrice?: NullableNumberInput;
      notes?: string | null;
      changedBy?: string | null;
    }> | null;
  }): MasterDataProductPriceHistory {
    const summary = raw.summary ?? {};
    return new MasterDataProductPriceHistory({
      product: {
        productId: raw.product?.productId ?? 0,
        productKey: toTextOrNull(raw.product?.productKey),
        itemName: toTextOrNull(raw.product?.itemName),
        upcItemNumber: toTextOrNull(raw.product?.upcItemNumber),
        categoryName: toTextOrNull(raw.product?.categoryName),
      },
      purchaserId: raw.purchaserId ?? null,
      period: raw.period ?? "18",
      periodStart: toTextOrNull(raw.periodStart),
      periodEnd: toTextOrNull(raw.periodEnd),
      summary: {
        productPrice: toNumberOrNull(summary.productPrice),
        effectiveFrom: toTextOrNull(summary.effectiveFrom),
        nextScheduledDate: toTextOrNull(summary.nextScheduledDate),
        nextScheduledPrice: toNumberOrNull(summary.nextScheduledPrice),
        inheritedFromBase: summary.inheritedFromBase === true,
        appliesTo: toTextOrNull(summary.appliesTo),
      },
      items: (raw.items ?? []).map((item) => ({
        productPriceId: item.productPriceId ?? 0,
        effectiveFrom: toTextOrNull(item.effectiveFrom),
        effectiveTo: toTextOrNull(item.effectiveTo),
        purchaserId: item.purchaserId ?? null,
        appliesTo: toTextOrNull(item.appliesTo),
        oldPrice: toNumberOrNull(item.oldPrice),
        productPrice: toNumberOrNull(item.productPrice),
        notes: toTextOrNull(item.notes),
        changedBy: toTextOrNull(item.changedBy),
      })),
    });
  }
}

export class CreatedMasterDataProductPrice {
  private readonly props: ICreatedMasterDataProductPrice;

  private constructor(props: ICreatedMasterDataProductPrice) {
    this.props = props;
  }

  get productPriceId(): number {
    return this.props.productPriceId;
  }
  get productId(): number {
    return this.props.productId;
  }
  get purchaserId(): number | null {
    return this.props.purchaserId;
  }
  get productPrice(): number {
    return this.props.productPrice;
  }
  get startDate(): string | null {
    return this.props.startDate;
  }

  static from(raw: {
    productPriceId?: number | null;
    productId?: number | null;
    purchaserId?: number | null;
    productPrice?: NullableNumberInput;
    startDate?: string | null;
  }): CreatedMasterDataProductPrice {
    return new CreatedMasterDataProductPrice({
      productPriceId: raw.productPriceId ?? 0,
      productId: raw.productId ?? 0,
      purchaserId: raw.purchaserId ?? null,
      productPrice: Number(raw.productPrice ?? 0),
      startDate: toTextOrNull(raw.startDate),
    });
  }
}
